import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Loader2, RefreshCw, Package, ShieldAlert, Upload } from "lucide-react";

const SAMPLE = `@ROKET classify

Please classify and prepare an import declaration (4000) for the following shipment:

Goods: 500 pairs of leather footwear with genuine leather uppers
Origin: Vietnam
Destination: Slovenia
Quantity: 500 pairs
Gross mass: 1250 kg
Customs value: EUR 18,500
Importer: EUROPACIFIC d.o.o.
Exporter: Saigon Leather Co.
Incoterm: FOB Ho Chi Minh City

Thanks,
Broker Desk`;

export default function Dashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "@ROKET classify — Import shipment", body: SAMPLE, from_email: "broker@example.com", trigger: "classify" });

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["tickets"],
    queryFn: async () => (await api.get("/tickets")).data,
    refetchInterval: 8000,
  });

  const simulate = useMutation({
    mutationFn: async () => (await api.post("/tickets/simulate", form)).data,
    onSuccess: (t) => {
      toast.success(`Ticket ${t.ticket_id} processed`);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      navigate(`/tickets/${t.ticket_id}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const uploadNew = useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      return (await api.post("/documents/new-ticket", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      toast.success(`Ticket ${d.ticket_id} created from ${d.documents.length} document(s)`);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      navigate(`/tickets/${d.ticket_id}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"),
  });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-4 sticky top-0 bg-background z-10">
        <h1 className="font-heading text-lg font-semibold tracking-tight">Ticket Queue</h1>
        <span className="text-xs font-mono text-zinc-500">{tickets.length} tickets</span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="refresh-tickets" onClick={() => qc.invalidateQueries({ queryKey: ["tickets"] })}
            className="p-2 border border-zinc-800 rounded-sm hover:bg-zinc-900 text-zinc-400"><RefreshCw className="h-4 w-4" /></button>
          <label data-testid="new-from-docs-btn"
            className="flex items-center gap-2 border border-zinc-700 hover:bg-zinc-900 text-zinc-200 text-sm px-3 py-2 rounded-sm transition-colors duration-150 cursor-pointer">
            {uploadNew.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} New from documents
            <input type="file" multiple className="hidden" accept=".pdf,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp"
              onChange={(e) => e.target.files?.length && uploadNew.mutate(e.target.files)} />
          </label>
          <button data-testid="simulate-email-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-sm transition-colors duration-150">
            <Plus className="h-4 w-4" /> Simulate @ROKET email
          </button>
        </div>
      </header>

      <div className="p-6">
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Ticket ID</th>
                <th className="text-left font-medium py-2 px-3">Procedure</th>
                <th className="text-left font-medium py-2 px-3">Goods</th>
                <th className="text-left font-medium py-2 px-3">Origin</th>
                <th className="text-left font-medium py-2 px-3">HS Code</th>
                <th className="text-left font-medium py-2 px-3">Compliance</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="py-10 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
              {!isLoading && tickets.length === 0 && (
                <tr><td colSpan={7} className="py-16 text-center text-zinc-500">
                  <Package className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm">No tickets yet. Simulate an @ROKET email to run the pipeline.</p>
                </td></tr>
              )}
              {tickets.map((t) => (
                <tr key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`} onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                  className="border-t border-zinc-800/60 hover:bg-zinc-900 cursor-pointer transition-colors duration-150">
                  <td className="py-2 px-3 font-mono text-blue-400">{t.ticket_id}</td>
                  <td className="py-2 px-3 font-mono text-zinc-300">{t.procedure}</td>
                  <td className="py-2 px-3 text-zinc-300 max-w-[240px] truncate">{t.goods_description || "—"}</td>
                  <td className="py-2 px-3 font-mono text-zinc-400">{t.origin_country || "—"}</td>
                  <td className="py-2 px-3 font-mono text-zinc-200">{t.classification?.chosen_hs_code || "—"}</td>
                  <td className="py-2 px-3">
                    {(t.compliance || []).some((c) => c.severity === "high")
                      ? <span className="inline-flex items-center gap-1 text-red-400 text-xs"><ShieldAlert className="h-3.5 w-3.5" />{t.compliance.length}</span>
                      : <span className="text-zinc-500 text-xs">{(t.compliance || []).length || "—"}</span>}
                  </td>
                  <td className="py-2 px-3"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-zinc-950/80 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-semibold mb-1">Simulate incoming @ROKET email</h3>
            <p className="text-xs text-zinc-500 mb-4">Runs the full pipeline: parse → classify → duty → compliance → report.</p>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Subject</label>
            <input data-testid="sim-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full mb-3 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">From</label>
                <input data-testid="sim-from" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
              </div>
              <div className="w-32">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Trigger</label>
                <select data-testid="sim-trigger" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none">
                  <option value="classify">classify</option>
                  <option value="4000">4000</option>
                  <option value="4200">4200</option>
                  <option value="7100">7100</option>
                  <option value="T1">T1</option>
                  <option value="export">export</option>
                </select>
              </div>
            </div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Body</label>
            <textarea data-testid="sim-body" rows={9} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full mb-4 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs font-mono focus:border-blue-500 outline-none resize-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-800">Cancel</button>
              <button data-testid="sim-run" disabled={simulate.isPending} onClick={() => simulate.mutate()}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
                {simulate.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Run pipeline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
